import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Grid, TextField } from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { includes } from 'lodash';
import moment from 'moment';
import {
  AccountData,
  BulkRedeemSession,
  OrderData,
  OrderPageTypes,
  OrderService,
  PassOut,
  PassService,
} from '../../../api';
import { useEditOrder } from './EditOrderContext';
import { useAuth } from '../../../session/InternalAuthProvider';
import { useLanguage } from '../../../common/GeneralUtils';
import { findMultipleTicketsUiObject } from '../../../common/multipleTicketsUtils';
import { getOrderPages, getPassOrderPage } from '../../../utils/AccountUtils';
import { ChooseWorkshopSelect } from './components/chooseWorkshopSelect/ChooseWorkshopSelect';
import { DateAndTimePanel } from './components/dateAndTimePanel/DateAndTimePanel';
import { BulkSessionsPanel } from './components/bulkSessionsPanel/BulkSessionsPanel';
import { PassSearchPanel } from './components/passSearchPanel/PassSearchPanel';
import {
  PassRedemptionMode,
  PassRedemptionModePanel,
} from './components/passRedemptionModePanel/PassRedemptionModePanel';
import { EditOrderDialogFooter, EditOrderDialogIntent } from './components/footer/EditOrderDialogFooter';
import { EditOrderPanels } from './EditOrderTypes';
import { useEditOrderDates } from './hooks/useEditOrderDates';
import { DATE_FORMAT } from '../../calendarWrapper/CalendarWrapper';

const NINE_AM = 60 * 60 * 9;

type RedeemPassFormValues = {
  comment: string;
  date: string;
  timeSeconds: number;
  selectedUsersIds: string[];
};

export const RedeemPassDialogContent = ({
  expandedPanel,
  setExpandedPanel,
  addOrder,
  closeEditOrderDialog,
  editOrderDialogState,
  initialPass,
}: {
  expandedPanel?: EditOrderPanels;
  setExpandedPanel: (panelName?: EditOrderPanels) => void;
  addOrder?: (order: OrderData) => Promise<void>;
  closeEditOrderDialog: any;
  editOrderDialogState: any;
  initialPass?: PassOut;
}) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { parentId } = editOrderDialogState;

  // @ts-ignore
  const { editOrderState, setOrderPage } = useEditOrder();

  const isEventOrCourse = includes(
    [OrderPageTypes.COURSE, OrderPageTypes.EVENT],
    editOrderState.orderPage.order_page_type,
  );

  const orderPages = getOrderPages(authState.account);

  const [selectedPass, setSelectedPass] = useState<PassOut | undefined>(initialPass);
  const [redemptionMode, setRedemptionMode] = useState<PassRedemptionMode>(PassRedemptionMode.Single);
  const [bulkSessions, setBulkSessions] = useState<BulkRedeemSession[]>([]);
  const [redeemPassError, setRedeemPassError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const coveredOrderPageIds = useMemo(() => {
    if (!selectedPass) return undefined;
    const passOrderPage = getPassOrderPage(account, selectedPass.order_page_id);
    return passOrderPage?.redeemable_on?.map((item) => item.order_page_id);
  }, [selectedPass, account]);

  useEffect(() => {
    if (!coveredOrderPageIds?.length) return;
    if (coveredOrderPageIds.includes(editOrderState.orderPage.id)) return;
    setOrderPage(coveredOrderPageIds[0]);
    setExpandedPanel(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coveredOrderPageIds]);

  const multipleTicketsUi = useMemo(
    () => findMultipleTicketsUiObject(editOrderState.orderPage),
    [editOrderState.orderPage],
  );

  const targetTicket = useMemo(() => {
    if (!selectedPass || !multipleTicketsUi) return undefined;
    const passOrderPage = getPassOrderPage(account, selectedPass.order_page_id);
    const redeemableRow = passOrderPage?.redeemable_on?.find((r) => r.order_page_id === editOrderState.orderPage.id);
    if (!redeemableRow?.ticket_type_id) return undefined;
    return multipleTicketsUi.tickets.find((t) => t.id === redeemableRow.ticket_type_id);
  }, [selectedPass, multipleTicketsUi, account, editOrderState.orderPage.id]);

  const remainingPunches = useMemo(() => {
    if (!selectedPass) return 0;
    const passOrderPage = getPassOrderPage(account, selectedPass.order_page_id);
    const totalPunches = selectedPass.num_punches_override ?? passOrderPage?.num_punches;
    if (totalPunches == null) return Infinity;
    return totalPunches - (selectedPass.redeemed_order_ids?.length ?? 0);
  }, [selectedPass, account]);

  const initialDate = moment().format(DATE_FORMAT);

  const { availableDatesForEvents, isLoadingDates } = useEditOrderDates({
    isEventOrCourse,
    accountId: editOrderState.account.id,
    orderPageId: editOrderState.orderPage.id,
    orderDetails: editOrderState.details,
    currentDate: initialDate,
  });

  const getNewParentId = (date: string, timeSeconds: number) => {
    const timeFound = availableDatesForEvents
      .find((x) => x.date === date)
      ?.times.find((t) => t.seconds === timeSeconds);
    if (timeFound?.eventId && timeFound?.eventId !== parentId) {
      return timeFound.eventId;
    }
    return parentId;
  };

  const getCourseId = (date: string, timeSeconds: number) =>
    availableDatesForEvents.find((x) => x.date === date)?.times.find((t) => t.seconds === timeSeconds)?.courseId;

  const handlePanelChange = (panel: EditOrderPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : undefined);
  };

  const initialValues: RedeemPassFormValues = {
    comment: '',
    date: initialDate,
    timeSeconds: NINE_AM,
    selectedUsersIds: [],
  };

  const onSubmit = async (values: RedeemPassFormValues) => {
    if (!selectedPass) {
      setRedeemPassError(t('passes.no_pass_selected'));
      return;
    }
    setRedeemPassError(undefined);
    setLoading(true);
    try {
      if (redemptionMode === PassRedemptionMode.Bulk) {
        await PassService.adminBulkRedeemPass({
          pass_id: selectedPass.id,
          sessions: bulkSessions,
        });
      } else {
        const orderResp = await OrderService.createOrder({
          parent_id: isEventOrCourse ? getNewParentId(values.date, values.timeSeconds) : null,
          course_id: getCourseId(values.date, values.timeSeconds),
          details: { ...editOrderState.details, persons: 1 },
          date_and_time_details: !isEventOrCourse ? { date: values.date, time: values.timeSeconds } : undefined,
          personal_details: {
            firstname: selectedPass.firstname,
            lastname: selectedPass.lastname,
            email: selectedPass.email,
            phone: selectedPass.phone,
          },
          account_id: editOrderState.account.id,
          experience_id: editOrderState.experience.id,
          order_page_id: editOrderState.orderPage.id,
          language,
          employee_ids: values.selectedUsersIds,
          comment: values.comment,
          pass_id_to_redeem: selectedPass.id,
        });
        if (addOrder) await addOrder(orderResp);
      }
      closeEditOrderDialog();
    } catch (err: any) {
      setRedeemPassError(err?.body?.detail ?? err?.message ?? 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik<RedeemPassFormValues>
      initialValues={initialValues}
      validationSchema={yup.object().shape({})}
      onSubmit={onSubmit}>
      {({ values, setFieldValue }) => (
        <>
          <Grid
            container
            flexDirection="column"
            gap={1.5}
            sx={{
              width: 'auto',
              marginInline: '-4px',
              paddingInline: '4px',
              paddingBlockStart: '4px',
              marginBlockStart: '-4px',
              '& > .MuiAccordion-root + .MuiAccordion-root, & > .MuiAccordion-root + .MuiAccordion-root.Mui-expanded': {
                marginTop: 0,
              },
            }}
            style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}
            flexWrap="nowrap">
            <Grid container flexDirection="column" gap={1.5}>
              <PassSearchPanel selectedPass={selectedPass} onSelectPass={setSelectedPass} />
              {selectedPass && <PassRedemptionModePanel mode={redemptionMode} onModeChange={setRedemptionMode} />}
            </Grid>
            {selectedPass && (
              <Grid container flexDirection="column">
                <ChooseWorkshopSelect
                  orderPages={orderPages}
                  editOrderState={editOrderState}
                  setOrderPage={setOrderPage}
                  setExpandedPanel={setExpandedPanel}
                  coveredOrderPageIds={coveredOrderPageIds}
                />
              </Grid>
            )}
            {selectedPass && multipleTicketsUi && (
              <Grid container>
                {targetTicket ? (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    {t('passes.will_redeem_ticket_type', {
                      ticket: targetTicket.label[language] || targetTicket.id,
                    })}
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    {t('passes.pass_template_missing_ticket_type')}
                  </Alert>
                )}
              </Grid>
            )}
            {selectedPass && redemptionMode === PassRedemptionMode.Single && (
              <DateAndTimePanel
                availableDatesForEvents={availableDatesForEvents}
                isLoadingDates={isLoadingDates}
                expanded={expandedPanel}
                handleChange={handlePanelChange}
              />
            )}
            {selectedPass && redemptionMode === PassRedemptionMode.Bulk && (
              <BulkSessionsPanel
                orderPageId={editOrderState.orderPage.id}
                onCheckedSessionsChange={setBulkSessions}
                remainingPunches={remainingPunches}
                expanded={expandedPanel}
                handleChange={handlePanelChange}
              />
            )}
            <Grid container>
              <TextField
                fullWidth
                multiline
                label={t('calendar.edit_event.comments')}
                value={values.comment}
                onChange={(e) => setFieldValue('comment', e.target.value)}
              />
            </Grid>
          </Grid>
          <EditOrderDialogFooter
            editOrderDialogIntent={EditOrderDialogIntent.RedeemPass}
            loading={loading}
            isEditOrder={false}
            onConvertToVoucher={() => {}}
            extraError={redeemPassError}
          />
        </>
      )}
    </Formik>
  );
};
